// @ts-nocheck
import { ContainerModules, CourseContainerData } from '@yandex-int/k-common';
import cx from 'classnames';
import groupBy from 'lodash/groupBy';
import { Spin, useGeneratePath, useStaticUrls } from 'platform-components';
import qs from 'query-string';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation, useParams } from 'react-router';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { toggleModal, loadLessonViewData } from 'common.components/lesson-view.store/actions';
import lessonActions from 'common.components/lesson/actions';

import { CourseContainerHeader } from './__header/course-container__header';
import { CourseContainerAbout } from './__info/course-container__about';
import { CourseContainerModules } from './__modules/course-container__modules';
import { CourseContainerParagraphs } from './__paragraphs/course-container__paragraphs';
import { actions } from './actions';
import { useCourseContainersQuery, useModulesQuery } from './queries';

import './course-container.scss';

interface Params {
    containerSlug: string;
}

interface Props {
    className?: string;
    isPreview?: boolean;
}

// eslint-disable-next-line complexity
export const LibraryCourseContainer = ({ className, isPreview }: Props) => {
    const { containerSlug } = useParams<Params>();
    const { labLibrarySubject } = useStaticUrls();
    const generatePath = useGeneratePath();
    const courseId = useSelector(getCurrentYearCourseId);
    const modulesQuery = useModulesQuery(containerSlug);
    const courseContainersQuery = useCourseContainersQuery(courseId);

    const hasNoModules = modulesQuery.isSuccess && modulesQuery.data?.cmodules.length === 0;
    const courseContainer = useMemo(
        () => courseContainersQuery.data?.find((cc) => cc.container.slug === containerSlug),
        [courseContainersQuery.data, containerSlug]
    );
    const invalidSlug = !isPreview && courseContainersQuery.isSuccess && !courseContainer;

    useOpenLessonModalFromSearchParams();

    const groups = useMemo(
        () => Object.entries(groupBy(modulesQuery.data?.cmodules || [], 'group_name')),
        [modulesQuery.data?.cmodules]
    );

    const openedModule = useOpenModuleFromLastAssignmentOrSearchParams(
        courseId,
        courseContainersQuery.isSuccess,
        courseContainer,
        modulesQuery.isSuccess,
        modulesQuery.data
    );

    const openModuleOrder = useMemo(
        () => groups.map((group) => group[0]).indexOf(openedModule?.group_name || '') + 1,
        [groups, openedModule]
    );

    if (!isPreview && (invalidSlug || hasNoModules)) {
        return <Redirect to={`${generatePath(labLibrarySubject)}`} />;
    }

    return (
        <div className={cx('course-container', className)}>
            <CourseContainerHeader
                isPreview={isPreview}
                moduleOrder={openModuleOrder}
                title={openedModule?.group_name}
            />
            {/* eslint-disable-next-line no-nested-ternary */}
            {(courseId && !courseContainersQuery.isSuccess) || !modulesQuery.isSuccess ? (
                <Spin />
            ) : openedModule && modulesQuery.data ? (
                <CourseContainerModules modules={modulesQuery.data} />
            ) : (
                <>
                    <CourseContainerAbout slug={containerSlug} />
                    <CourseContainerParagraphs
                        key={String(courseId)}
                        modulesQuery={modulesQuery}
                        titlesOnly={isPreview}
                    />
                </>
            )}
        </div>
    );
};

function useOpenLessonModalFromSearchParams() {
    const { search } = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const courseId = useSelector(getCurrentYearCourseId);

    useEffect(() => {
        const {
            lessonId, // выход из Вывода на доску
            preparedLessonId, // диплинк на lesson в курс-контейнере
            ...restSearch
        } = qs.parse(search);

        if (lessonId && courseId) {
            dispatch(
                lessonActions.getFullClessonRequest({
                    clessonId: Number(lessonId),
                    courseId,
                })
            );
            dispatch(toggleModal(true));
            history.replace({ search: qs.stringify(restSearch) });
        } else if (preparedLessonId) {
            const moduleId = restSearch.moduleId;
            dispatch(
                loadLessonViewData({
                    lessonId: preparedLessonId,
                    courseId,
                    moduleId,
                })
            );
            dispatch(toggleModal(true));
            history.replace({ search: qs.stringify(restSearch) });
        }
    }, [courseId, dispatch, history, search]);
}

// eslint-disable-next-line max-params
export function useOpenModuleFromLastAssignmentOrSearchParams(
    courseId: number | null,
    isCourseContainerQuerySuccess: boolean,
    courseContainer?: CourseContainerData,
    isModulesQuerySuccess?: boolean,
    modules?: ContainerModules
) {
    const { search } = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const params = qs.parse(search);
    const moduleId = Number(params.module_id);

    useEffect(() => {
        dispatch(actions.setOpenedModuleId({ moduleId }));
    }, [dispatch, history, moduleId]);

    return modules ? modules.cmodules.find((m) => m.module.id === moduleId) : null;
}
